import { type V2, angleDiff, angleTo, clamp, dirFromAngle, dist, lerp, wrapAngle } from '../../core/math';
import type { AccountSave } from '../../game/account';
import type { CharacterSave } from '../../game/character';
import { type Cost, canAfford, pay } from '../../game/inventory';
import {
  LAST_PORT_FLAG,
  PORTS,
  SAILOR_BY_ID,
  SHIP_MAX_LEVEL,
  TOW_HULL,
  type ShipLevelInfo,
  repairRate,
  shipLevelInfo,
  towFee,
  upgradeCost,
} from './data';

/**
 * Ship math with no engine dependencies: effective stats (level + crew), the
 * steering controller (turn-rate-limited heading with momentum) and hull
 * bookkeeping (wear, repair, refits, crew, towing).
 */

export type ShipSave = AccountSave['ship'];

export interface ShipStats extends ShipLevelInfo {
  level: number;
  /** 0..1 extra visibility inside fog banks. */
  fogSight: number;
  /** Fraction off harbour repairs. */
  repairDiscount: number;
  /** Extra salvage silver (fraction). */
  salvageBonus: number;
  /** Fraction off the cannon cooldown. */
  cannonCdr: number;
}

/** Effective stats for a ship level and its signed-on crew (unknown or surplus sailors are ignored). */
export function shipStats(level: number, crew: readonly string[] = []): ShipStats {
  const base = shipLevelInfo(level);
  const add = { speed: 0, turn: 0, hull: 0, storm: 0, fog: 0, repair: 0, salvage: 0, cannon: 0 };
  const seen = new Set<string>();
  for (const id of crew) {
    const s = SAILOR_BY_ID[id];
    if (!s || seen.has(id) || seen.size >= base.crewSlots) continue;
    seen.add(id);
    add[s.bonus] += s.value;
  }
  return {
    ...base,
    level: Math.max(1, Math.min(SHIP_MAX_LEVEL, Math.round(level))),
    maxSpeed: base.maxSpeed * (1 + add.speed),
    turnRate: base.turnRate * (1 + add.turn),
    maxHull: Math.round(base.maxHull * (1 + add.hull)),
    stormResist: Math.min(0.75, base.stormResist + add.storm),
    fogSight: Math.min(1, add.fog),
    repairDiscount: Math.min(0.6, add.repair),
    salvageBonus: add.salvage,
    cannonCdr: Math.min(0.6, add.cannon),
  };
}

/**
 * The account's ship, created and sanitised in place (old saves, bad data):
 * level in range, known unique crew within the berths, hull within [0, max].
 */
export function shipOf(account: AccountSave): ShipSave {
  let ship = (account as { ship?: ShipSave | null }).ship;
  if (!ship || typeof ship !== 'object') {
    ship = { level: 1, hull: Number.NaN, crew: [] };
    account.ship = ship;
  }
  const level = clamp(Math.round(Number(ship.level) || 1), 1, SHIP_MAX_LEVEL);
  if (ship.level !== level) ship.level = level;
  const slots = shipLevelInfo(level).crewSlots;
  const crew: unknown = ship.crew;
  const valid = Array.isArray(crew) && crew.length <= slots && crew.every((id, i) => typeof id === 'string' && !!SAILOR_BY_ID[id] && crew.indexOf(id) === i);
  if (!valid) {
    const clean: string[] = [];
    for (const id of Array.isArray(crew) ? (crew as unknown[]) : []) if (typeof id === 'string' && SAILOR_BY_ID[id] && !clean.includes(id) && clean.length < slots) clean.push(id);
    ship.crew = clean;
  }
  const max = shipStats(level, ship.crew).maxHull;
  const h = Number(ship.hull);
  const hull = Number.isFinite(h) ? clamp(h, 0, max) : max;
  if (ship.hull !== hull) ship.hull = hull;
  return ship;
}

export const statsOf = (ship: ShipSave): ShipStats => shipStats(ship.level, ship.crew);

// ---------------------------------------------------------------- steering

export interface ShipMotion {
  /** Facing angle, same convention as `Actor.facing` (0 = +Z). */
  heading: number;
  /** Forward speed, m/s (ships don't reverse). */
  speed: number;
  /** Current rate of turn, rad/s (the rudder's momentum). */
  turnVel: number;
  /** Where the helm is steering (null = coast). */
  target: V2 | null;
}

export const newMotion = (heading = 0): ShipMotion => ({ heading, speed: 0, turnVel: 0, target: null });

export interface SteerMods {
  speedMult?: number;
  accelMult?: number;
  turnMult?: number;
  /** Multiplier on coasting drag / braking (towing, running aground). */
  drag?: number;
}

/** The target counts as reached inside this radius. */
export const ARRIVE_RADIUS = 3;
/** Angular acceleration of the rudder, rad/s². */
export const RUDDER_ACCEL = 2.6;
/** Coasting drag and braking deceleration, m/s². */
export const DRAG = 1.6;
export const BRAKE = 2.4;
/** Turn authority at a standstill (fraction of the turn rate). */
export const STEERAGE = 0.45;
const STEER_GAIN = 2.2;

/** Turn rate available at the current speed (less rudder authority when slow). */
export function turnAuthority(m: ShipMotion, stats: Pick<ShipStats, 'maxSpeed' | 'turnRate'>, turnMult = 1): number {
  const k = STEERAGE + (1 - STEERAGE) * Math.min(1, m.speed / Math.max(0.1, 0.5 * stats.maxSpeed));
  return stats.turnRate * turnMult * k;
}

/**
 * Advances the helm by `dt`: the rudder eases the rate of turn toward the
 * target bearing (never beyond the turn rate), the sails accelerate toward a
 * desired speed that drops for sharp turns and on the final approach, and
 * with no target the ship coasts to a stop. Returns the planar displacement
 * before collisions and currents.
 */
export function steer(m: ShipMotion, pos: V2, stats: Pick<ShipStats, 'maxSpeed' | 'accel' | 'turnRate'>, dt: number, mods: SteerMods = {}): V2 {
  const vmax = stats.maxSpeed * (mods.speedMult ?? 1);
  let desired = 0;
  let err = 0;
  if (m.target && dist(pos, m.target) <= ARRIVE_RADIUS) m.target = null;
  if (m.target) {
    const d = dist(pos, m.target);
    err = angleDiff(m.heading, angleTo(pos, m.target));
    // Target dead astern: keep turning the way the rudder already swings instead of dithering at ±π.
    if (Math.abs(err) > 2.8 && Math.abs(m.turnVel) > 0.05 && Math.sign(err) !== Math.sign(m.turnVel)) err += Math.sign(m.turnVel) * 2 * Math.PI;
    const approach = Math.sqrt(2 * BRAKE * Math.max(0, d - ARRIVE_RADIUS * 0.5));
    desired = Math.min(vmax, approach) * lerp(1, 0.35, Math.min(1, Math.abs(err) / Math.PI));
  }
  const rate = turnAuthority(m, stats, mods.turnMult);
  const want = m.target ? clamp(err * STEER_GAIN, -rate, rate) : 0;
  m.turnVel = clamp(m.turnVel + clamp(want - m.turnVel, -RUDDER_ACCEL * dt, RUDDER_ACCEL * dt), -rate, rate);
  m.heading = wrapAngle(m.heading + m.turnVel * dt);
  if (m.speed < desired) m.speed = Math.min(desired, m.speed + stats.accel * (mods.accelMult ?? 1) * dt);
  else m.speed = Math.max(desired, m.speed - (m.target ? BRAKE : DRAG) * (mods.drag ?? 1) * dt);
  const f = dirFromAngle(m.heading);
  return { x: f.x * m.speed * dt, z: f.z * m.speed * dt };
}

// ---------------------------------------------------------------- hull

/** Removes hull (never below 0); returns what was actually lost. */
export function damageHull(ship: ShipSave, amount: number): number {
  if (!(amount > 0)) return 0;
  const lost = Math.min(ship.hull, amount);
  ship.hull -= lost;
  return lost;
}

/** Adds hull up to the maximum; returns what was restored. */
export function healHull(ship: ShipSave, amount: number, stats: ShipStats = statsOf(ship)): number {
  if (!(amount > 0)) return 0;
  const add = Math.min(stats.maxHull - ship.hull, amount);
  if (add <= 0) return 0;
  ship.hull += add;
  return add;
}

export interface RepairQuote {
  /** Whole hull points missing. */
  missing: number;
  /** Silver per point after crew discounts. */
  rate: number;
  cost: number;
}

export function repairQuote(ship: ShipSave, stats: ShipStats = statsOf(ship)): RepairQuote {
  const missing = Math.max(0, Math.ceil(stats.maxHull - ship.hull - 1e-6));
  const rate = repairRate(ship.level) * (1 - stats.repairDiscount);
  return { missing, rate, cost: Math.ceil(missing * rate) };
}

/**
 * Harbour repair: pays silver for as much hull as the wallet covers (full
 * repair when it can). Returns the points repaired and the silver spent.
 */
export function repairShip(account: AccountSave): { repaired: number; cost: number } {
  const ship = shipOf(account);
  const stats = statsOf(ship);
  const q = repairQuote(ship, stats);
  if (q.missing <= 0) return { repaired: 0, cost: 0 };
  const affordable = q.cost <= account.wallet.silver ? q.missing : Math.floor(account.wallet.silver / q.rate);
  if (affordable <= 0) return { repaired: 0, cost: 0 };
  const cost = affordable === q.missing ? q.cost : Math.ceil(affordable * q.rate);
  account.wallet.silver -= cost;
  const repaired = affordable === q.missing ? stats.maxHull - ship.hull : affordable;
  ship.hull = Math.min(stats.maxHull, ship.hull + repaired);
  return { repaired, cost };
}

/** Shipwright refit: pays silver + materials, raises the level and adds the new hull capacity. */
export function upgradeShip(char: CharacterSave, account: AccountSave): boolean {
  const ship = shipOf(account);
  const cost = upgradeCost(ship.level);
  if (!cost || !pay(char, account, cost)) return false;
  const before = statsOf(ship).maxHull;
  ship.level += 1;
  ship.hull = Math.min(statsOf(ship).maxHull, ship.hull + (statsOf(ship).maxHull - before));
  return true;
}

export const canUpgrade = (char: CharacterSave, account: AccountSave): boolean => {
  const cost: Cost | null = upgradeCost(shipOf(account).level);
  return !!cost && canAfford(char, account, cost);
};

export type HireResult = 'ok' | 'unknown' | 'aboard' | 'full' | 'silver';

/** Signs a sailor on for their fee. */
export function hireSailor(account: AccountSave, id: string): HireResult {
  const ship = shipOf(account);
  const s = SAILOR_BY_ID[id];
  if (!s) return 'unknown';
  if (ship.crew.includes(id)) return 'aboard';
  if (ship.crew.length >= shipLevelInfo(ship.level).crewSlots) return 'full';
  if (account.wallet.silver < s.cost) return 'silver';
  account.wallet.silver -= s.cost;
  const before = statsOf(ship).maxHull;
  ship.crew.push(id);
  // A carpenter's extra capacity arrives sound.
  ship.hull = Math.min(statsOf(ship).maxHull, ship.hull + Math.max(0, statsOf(ship).maxHull - before));
  return 'ok';
}

/** Sends a sailor ashore (no refund); the hull is clamped to the new maximum. */
export function dismissSailor(account: AccountSave, id: string): boolean {
  const ship = shipOf(account);
  const i = ship.crew.indexOf(id);
  if (i < 0) return false;
  ship.crew.splice(i, 1);
  ship.hull = Math.min(ship.hull, statsOf(ship).maxHull);
  return true;
}

/** The harbour a broken ship is towed to: the last port visited (Havenreach by default). */
export function lastPort(char: CharacterSave | null | undefined): string {
  const v = char?.flags[LAST_PORT_FLAG];
  return typeof v === 'string' && v in PORTS ? v : 'havenreach';
}

/**
 * Settles a tow: the tug fee (whatever silver the captain has, up to the fee)
 * and an emergency patch to half hull. A `free` tow (the ship never left
 * harbour seaworthy) changes nothing.
 */
export function towShip(account: AccountSave, free = false): { fee: number; hull: number } {
  const ship = shipOf(account);
  if (free) return { fee: 0, hull: ship.hull };
  const fee = Math.min(account.wallet.silver, towFee(ship.level));
  account.wallet.silver -= fee;
  ship.hull = Math.max(ship.hull, Math.ceil(statsOf(ship).maxHull * TOW_HULL));
  return { fee, hull: ship.hull };
}

/** Compass bearing in degrees (0 = north = screen up = −Z, 90 = east = +X). */
export function bearingDeg(dx: number, dz: number): number {
  const d = (Math.atan2(dx, -dz) * 180) / Math.PI;
  return (d + 360) % 360;
}

/** Compass heading of an actor facing angle. */
export const headingDeg = (facing: number): number => bearingDeg(Math.sin(facing), Math.cos(facing));
